import React, { useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { router, type Href } from 'expo-router';
import { Home, type LucideIcon } from 'lucide-react-native';
import { useTheme, FONT_SIZES, SPACING, TYPOGRAPHY, CATEGORY_ICONS } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { AdaptiveSheet, Button } from '../common';

interface GuidanceItem {
  key: 'progress' | 'opportunity' | 'hardening';
  icon: LucideIcon;
  iconColor: string;
  title: string;
  body: string;
  why: string;
}

/**
 * Guidance shown before the observation composer (client ask, Sept 2026:
 * "guidance on what to look for and why"). Cloned from HowReDIWorksSheet's
 * hairline-row grammar. One-time per device
 * (uiStore.hasSeenObservationGuidance) — see useObservationGuidance for the
 * gate that opens this and the stashed composer path Continue resumes.
 * Also reachable on demand: the composer's own "What should I look for?"
 * link and a row in HowReDIWorksSheet both open it with no stashed path, so
 * Continue there just closes.
 */
export function ObservationGuidanceSheet() {
  const { colors } = useTheme();
  const open = useUIStore((s) => s.activeSheet === 'observationGuidance');
  const sheetOpenTick = useUIStore((s) => s.sheetOpenTick);
  const close = useUIStore((s) => s.closeSheet);
  const markObservationGuidanceSeen = useUIStore((s) => s.markObservationGuidanceSeen);
  const observationGuidanceNext = useUIStore((s) => s.observationGuidanceNext);
  const setObservationGuidanceNext = useUIStore((s) => s.setObservationGuidanceNext);

  const handleSheetDismissed = useCallback(() => {
    if (useUIStore.getState().activeSheet === 'observationGuidance') {
      close();
    }
  }, [close]);

  const handleContinue = useCallback(() => {
    markObservationGuidanceSeen();
    const next = observationGuidanceNext;
    setObservationGuidanceNext(null);
    close();
    if (next) router.push(next as Href);
  }, [markObservationGuidanceSeen, observationGuidanceNext, setObservationGuidanceNext, close]);

  const items: GuidanceItem[] = [
    {
      key: 'progress',
      icon: CATEGORY_ICONS.progress,
      iconColor: colors.progress,
      title: 'Progress',
      body: 'Any sign of thinning, limbing, or picking up fuel.',
      why: 'It shows neighbors and insurers that this area is being worked.',
    },
    {
      key: 'opportunity',
      icon: CATEGORY_ICONS.risk,
      iconColor: colors.opportunity,
      title: 'Opportunities',
      body: 'Dense trees, dead limbs, fuel on the ground.',
      why: 'Embers ignite fuel like this first, and a neighbor or a local crew can fix it.',
    },
    {
      key: 'hardening',
      icon: Home,
      iconColor: colors.primary,
      title: 'Home hardening',
      body: 'Vents, eaves, decks, and the first five feet around a structure.',
      why: 'Most homes are lost to embers, not flames, and these are where embers get in.',
    },
  ];

  return (
    <AdaptiveSheet
      isOpen={open}
      tick={sheetOpenTick}
      backdrop="light"
      onRequestClose={close}
      onDismiss={handleSheetDismissed}
      dialogWidth={480}>
      <AdaptiveSheet.ScrollView
        testID="observation-guidance-sheet"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.content}>
        <Text style={[styles.title, { color: colors.text }]}>What to look for</Text>
        <Text style={[styles.lead, { color: colors.textSecondary }]}>
          REDI involves the community in capturing observations about readiness. Here&apos;s what to
          look for:
        </Text>

        {items.map((item) => {
          const Icon = item.icon;
          return (
            <View
              key={item.key}
              testID={`observation-guidance-item-${item.key}`}
              style={[styles.row, { borderBottomColor: colors.border }]}>
              <View style={styles.rowIcon}>
                <Icon size={18} color={item.iconColor} />
              </View>
              <View style={styles.rowText}>
                <Text style={[styles.rowTitle, { color: colors.text }]}>{item.title}</Text>
                <Text style={[styles.rowBody, { color: colors.textSecondary }]}>{item.body}</Text>
                <Text style={[styles.rowWhy, { color: colors.textTertiary }]}>
                  Why it matters: {item.why}
                </Text>
              </View>
            </View>
          );
        })}

        <View style={styles.footer}>
          <Button
            title="Continue"
            onPress={handleContinue}
            variant="primary"
            fullWidth
            testID="observation-guidance-continue"
          />
        </View>

        {/* Scroll sentinel: Maestro flows assert this is reachable. */}
        <View testID="observation-guidance-end" style={styles.scrollSentinel} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  scrollSentinel: {
    height: 1,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  lead: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: SPACING.smd,
  },
  rowIcon: {
    marginTop: 2,
  },
  rowText: {
    flex: 1,
  },
  rowTitle: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
    marginBottom: 2,
  },
  rowBody: {
    ...TYPOGRAPHY.bodySmall,
  },
  rowWhy: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 4,
  },
  footer: {
    marginTop: SPACING.md,
  },
});
